import React, { useEffect, useState } from 'react'
import api from '../api'
import { Plus, Wifi, Wand2 } from 'lucide-react'
import { Card, PageHeader, Button, Input, Field, Modal, ModalHeader, Alert, Select, Label } from '../components/ui'

// Satu baris Upload/Download utk Generator Rate Limit (Max Limit, Burst Limit, Burst Threshold, Limit At
// semuanya berbentuk pasangan rx/tx dgn satuan Mbps/Kbps -- lihat komposeRateLimit di bawah)
function RateRow({ label, note, up, unitUp, down, unitDown, onUp, onUnitUp, onDown, onUnitDown }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <Label>{label}</Label>
      {note && <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: -3, marginBottom: 6 }}>{note}</div>}
      <div className="grid-2" style={{ gap: 10 }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Upload</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <Input type="number" min="0" step="any" value={up} onChange={e => onUp(e.target.value)} placeholder="0" />
            <Select value={unitUp} onChange={e => onUnitUp(e.target.value)} style={{ width: 84, flexShrink: 0 }}>
              <option value="M">Mbps</option>
              <option value="k">Kbps</option>
            </Select>
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Download</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <Input type="number" min="0" step="any" value={down} onChange={e => onDown(e.target.value)} placeholder="0" />
            <Select value={unitDown} onChange={e => onUnitDown(e.target.value)} style={{ width: 84, flexShrink: 0 }}>
              <option value="M">Mbps</option>
              <option value="k">Kbps</option>
            </Select>
          </div>
        </div>
      </div>
    </div>
  )
}

function defaultGen() {
  return {
    maxUp: '', maxUpUnit: 'M', maxDown: '', maxDownUnit: 'M',
    burstUp: '', burstUpUnit: 'M', burstDown: '', burstDownUnit: 'M',
    threshUp: '', threshUpUnit: 'M', threshDown: '', threshDownUnit: 'M',
    burstTimeUp: '', burstTimeDown: '',
    priority: '8',
    minUp: '', minUpUnit: 'M', minDown: '', minDownUnit: 'M',
  }
}

// "4M" -> {num:'4', unit:'M'}; "0" atau kosong -> num:'' (dianggap tidak diisi)
function parseValuePart(token) {
  const m = String(token || '').trim().match(/^([\d.]+)\s*(M|k)?$/i)
  if (!m || Number(m[1]) === 0) return { num: '', unit: 'M' }
  return { num: m[1], unit: (m[2] || '').toLowerCase() === 'k' ? 'k' : 'M' }
}

// Parse string rate-limit Mikrotik yg sudah ada (format 6 token: max burst thresh burst-time priority min)
// supaya field Generator ke-prefill saat Edit paket yg rate_limit-nya sudah diisi sebelumnya.
function parseRateLimit(str) {
  try {
    const tokens = String(str || '').trim().split(/\s+/)
    if (tokens.length < 6) return null
    const [max, burst, thresh, burstTime, priority, min] = tokens
    const [maxUp, maxDown] = max.split('/')
    const [burstUp, burstDown] = burst.split('/')
    const [threshUp, threshDown] = thresh.split('/')
    const [btUp, btDown] = burstTime.split('/')
    const [minUp, minDown] = min.split('/')
    const a = parseValuePart(maxUp), b = parseValuePart(maxDown)
    const c = parseValuePart(burstUp), d = parseValuePart(burstDown)
    const e = parseValuePart(threshUp), f = parseValuePart(threshDown)
    const g = parseValuePart(minUp), h = parseValuePart(minDown)
    return {
      maxUp: a.num, maxUpUnit: a.unit, maxDown: b.num, maxDownUnit: b.unit,
      burstUp: c.num, burstUpUnit: c.unit, burstDown: d.num, burstDownUnit: d.unit,
      threshUp: e.num, threshUpUnit: e.unit, threshDown: f.num, threshDownUnit: f.unit,
      burstTimeUp: Number(btUp) ? btUp : '', burstTimeDown: Number(btDown) ? btDown : '',
      priority: priority || '8',
      minUp: g.num, minUpUnit: g.unit, minDown: h.num, minDownUnit: h.unit,
    }
  } catch { return null }
}

function formatPart(num, unit) {
  const n = Number(num)
  if (!num || !n) return '0'
  return `${n}${unit === 'k' ? 'k' : 'M'}`
}

// Susun balik ke format Mikrotik: rx/tx rx/tx rx/tx rx/tx priority rx/tx (max, burst, threshold, burst-time, priority, min)
function composeRateLimit(g) {
  return [
    `${formatPart(g.maxUp, g.maxUpUnit)}/${formatPart(g.maxDown, g.maxDownUnit)}`,
    `${formatPart(g.burstUp, g.burstUpUnit)}/${formatPart(g.burstDown, g.burstDownUnit)}`,
    `${formatPart(g.threshUp, g.threshUpUnit)}/${formatPart(g.threshDown, g.threshDownUnit)}`,
    `${g.burstTimeUp || '0'}/${g.burstTimeDown || '0'}`,
    `${g.priority || '8'}`,
    `${formatPart(g.minUp, g.minUpUnit)}/${formatPart(g.minDown, g.minDownUnit)}`,
  ].join(' ')
}

export default function PaketLangganan() {
  const [data, setData] = useState([])
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState({ nama: '', kecepatan: '', harga_bulanan: '', urutan: 0, shared: '', rate_limit: '', masa_aktif: '' })
  const [editId, setEditId] = useState(null)
  const [error, setError] = useState('')
  const [genModal, setGenModal] = useState(false)
  const [gen, setGen] = useState(defaultGen())

  useEffect(() => { load() }, [])

  async function load() {
    const res = await api.get('/paket-langganan')
    setData(res.data)
  }

  function bukaTambah() {
    setForm({ nama: '', kecepatan: '', harga_bulanan: '', urutan: data.length + 1, shared: '', rate_limit: '', masa_aktif: '' })
    setEditId(null); setError(''); setModal(true)
  }

  function bukaEdit(p) {
    setForm({
      nama: p.nama, kecepatan: p.kecepatan || '', harga_bulanan: p.harga_bulanan, urutan: p.urutan,
      shared: p.shared || '', rate_limit: p.rate_limit || '', masa_aktif: p.masa_aktif || ''
    })
    setEditId(p.id); setError(''); setModal(true)
  }

  async function simpan() {
    if (!form.nama.trim()) { setError('Nama paket wajib diisi'); return }
    try {
      if (editId) {
        await api.put(`/paket-langganan/${editId}`, { ...form, aktif: 1 })
      } else {
        await api.post('/paket-langganan', form)
      }
      setModal(false); load()
    } catch (e) {
      setError(e.response?.data?.message || 'Gagal menyimpan')
    }
  }

  function bukaGenerator() {
    setGen(parseRateLimit(form.rate_limit) || defaultGen())
    setGenModal(true)
  }

  function terapkanGenerator() {
    setForm({ ...form, rate_limit: composeRateLimit(gen) })
    setGenModal(false)
  }

  async function nonaktifkan(id) {
    if (!window.confirm('Nonaktifkan paket langganan ini?')) return
    await api.delete(`/paket-langganan/${id}`)
    load()
  }

  const WARNA = ['#4f46e5', '#f72585', '#7c3aed', '#0ea5e9', '#10b981', '#f59e0b', '#4cc9f0', '#e11d48']

  return (
    <div>
      <PageHeader
        title="Paket Langganan"
        subtitle="Master data paket internet — kecepatan & harga bulanan, dipakai saat generate tagihan"
        actions={<Button icon={Plus} onClick={bukaTambah}>Tambah Paket</Button>}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
        {data.map((p, i) => (
          <Card key={p.id} style={{ padding: 18, opacity: p.aktif ? 1 : 0.5, display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <div style={{
              width: 46, height: 46, borderRadius: 11, flexShrink: 0,
              background: WARNA[i % WARNA.length],
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff'
            }}><Wifi size={20} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{p.nama}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{p.kecepatan || '-'}{p.shared ? ` — Shared ${p.shared}` : ''}</div>
              {p.rate_limit && <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 2, fontFamily: 'monospace' }}>{p.rate_limit}</div>}
              <div style={{ fontSize: 13, color: 'var(--brand)', marginTop: 4, fontWeight: 600 }}>Rp {Number(p.harga_bulanan).toLocaleString('id-ID')} / bulan</div>
              {p.masa_aktif && <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>Masa Aktif: {p.masa_aktif} Bulan</div>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Button size="sm" variant="soft" onClick={() => bukaEdit(p)}>Edit</Button>
              {p.aktif && <Button size="sm" variant="danger-soft" onClick={() => nonaktifkan(p.id)}>Hapus</Button>}
            </div>
          </Card>
        ))}
      </div>

      {modal && (
        <Modal onClose={() => setModal(false)} width={420}>
          <ModalHeader title={editId ? 'Edit Paket Langganan' : 'Tambah Paket Langganan'} onClose={() => setModal(false)} />
          {error && <Alert type="error">{error}</Alert>}
          <Field label="Nama Paket *" style={{ marginBottom: 14 }}>
            <Input value={form.nama} onChange={e => setForm({ ...form, nama: e.target.value })} placeholder="Paket 20 Mbps" />
          </Field>
          <div className="grid-2" style={{ gap: 10, marginBottom: 14 }}>
            <Field label="Kecepatan">
              <Input value={form.kecepatan} onChange={e => setForm({ ...form, kecepatan: e.target.value })} placeholder="20 Mbps" />
            </Field>
            <Field label="Shared">
              <Input value={form.shared} onChange={e => setForm({ ...form, shared: e.target.value })} placeholder="1:8" />
            </Field>
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <Label style={{ marginBottom: 0 }}>Mikrotik Rate Limit</Label>
              <Button type="button" size="sm" variant="soft" icon={Wand2} onClick={bukaGenerator}>Generator</Button>
            </div>
            <Input value={form.rate_limit} onChange={e => setForm({ ...form, rate_limit: e.target.value })} placeholder="4M/4M 0/0 0/0 0/0 8 0/0" style={{ fontFamily: 'monospace' }} />
          </div>
          <div className="grid-2" style={{ gap: 10, marginBottom: 14 }}>
            <Field label="Masa Aktif (Bulan)">
              <Input type="number" min="1" step="1" value={form.masa_aktif} onChange={e => setForm({ ...form, masa_aktif: e.target.value })} placeholder="1" />
            </Field>
            <Field label="Harga Bulanan (Rp) *">
              <Input type="number" value={form.harga_bulanan} onChange={e => setForm({ ...form, harga_bulanan: e.target.value })} placeholder="150000" />
            </Field>
          </div>
          <Field label="Urutan Tampil" style={{ marginBottom: 22 }}>
            <Input type="number" value={form.urutan} onChange={e => setForm({ ...form, urutan: parseInt(e.target.value) || 0 })} />
          </Field>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="neutral" onClick={() => setModal(false)} style={{ flex: 1, justifyContent: 'center' }}>Batal</Button>
            <Button onClick={simpan} style={{ flex: 1, justifyContent: 'center' }}>Simpan</Button>
          </div>
        </Modal>
      )}

      {genModal && (
        <Modal onClose={() => setGenModal(false)} width={640}>
          <ModalHeader
            title="Generator Rate Limit"
            subtitle="Isi per-kolom seperti form Mikrotik, nanti otomatis digabung jadi satu string rate-limit"
            onClose={() => setGenModal(false)}
          />
          <RateRow
            label="Max Limit" note="Batas kecepatan maksimal"
            up={gen.maxUp} unitUp={gen.maxUpUnit} down={gen.maxDown} unitDown={gen.maxDownUnit}
            onUp={v => setGen({ ...gen, maxUp: v })} onDown={v => setGen({ ...gen, maxDown: v })}
            onUnitUp={v => setGen({ ...gen, maxUpUnit: v })} onUnitDown={v => setGen({ ...gen, maxDownUnit: v })}
          />
          <RateRow
            label="Burst Limit" note="Kecepatan boost sementara sebelum turun ke Max Limit (opsional, kosongkan jika tidak dipakai)"
            up={gen.burstUp} unitUp={gen.burstUpUnit} down={gen.burstDown} unitDown={gen.burstDownUnit}
            onUp={v => setGen({ ...gen, burstUp: v })} onDown={v => setGen({ ...gen, burstDown: v })}
            onUnitUp={v => setGen({ ...gen, burstUpUnit: v })} onUnitDown={v => setGen({ ...gen, burstDownUnit: v })}
          />
          <RateRow
            label="Burst Threshold" note="Batas rata-rata pemakaian sebelum burst dimatikan (opsional)"
            up={gen.threshUp} unitUp={gen.threshUpUnit} down={gen.threshDown} unitDown={gen.threshDownUnit}
            onUp={v => setGen({ ...gen, threshUp: v })} onDown={v => setGen({ ...gen, threshDown: v })}
            onUnitUp={v => setGen({ ...gen, threshUpUnit: v })} onUnitDown={v => setGen({ ...gen, threshDownUnit: v })}
          />
          <div style={{ marginBottom: 14 }}>
            <Label>Burst Time (detik)</Label>
            <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: -3, marginBottom: 6 }}>Durasi burst boleh aktif sebelum dievaluasi ulang (opsional)</div>
            <div className="grid-2" style={{ gap: 10 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Upload</div>
                <Input type="number" min="0" value={gen.burstTimeUp} onChange={e => setGen({ ...gen, burstTimeUp: e.target.value })} placeholder="0" />
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Download</div>
                <Input type="number" min="0" value={gen.burstTimeDown} onChange={e => setGen({ ...gen, burstTimeDown: e.target.value })} placeholder="0" />
              </div>
            </div>
          </div>
          <RateRow
            label="Limit At (Min Limit)" note="Kecepatan minimum yang dijamin saat jaringan padat (opsional)"
            up={gen.minUp} unitUp={gen.minUpUnit} down={gen.minDown} unitDown={gen.minDownUnit}
            onUp={v => setGen({ ...gen, minUp: v })} onDown={v => setGen({ ...gen, minDown: v })}
            onUnitUp={v => setGen({ ...gen, minUpUnit: v })} onUnitDown={v => setGen({ ...gen, minDownUnit: v })}
          />
          <Field label="Priority" style={{ marginBottom: 20, maxWidth: 220 }}>
            <Select value={gen.priority} onChange={e => setGen({ ...gen, priority: e.target.value })}>
              {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                <option key={n} value={n}>{n}{n === 1 ? ' (tertinggi)' : n === 8 ? ' (default/tanpa prioritas)' : ''}</option>
              ))}
            </Select>
          </Field>
          <div style={{
            background: '#f8f8fb', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
            padding: '10px 12px', fontSize: 11.5, fontFamily: 'monospace', color: 'var(--text-muted)',
            marginBottom: 20, wordBreak: 'break-all'
          }}>
            Preview: {composeRateLimit(gen)}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="neutral" onClick={() => setGenModal(false)} style={{ flex: 1, justifyContent: 'center' }}>Batal</Button>
            <Button onClick={terapkanGenerator} style={{ flex: 1, justifyContent: 'center' }}>Terapkan ke Rate Limit</Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
